import { isPreviewTool, isToolEnabled, type ToolCategory } from "../config/toolToggles";
import { createAutomationApiTools } from "../tools/automationApiTools";
import { createConversionApiTools } from "../tools/conversionApiTools";
import { createFeatureApiTools } from "../tools/featureApiTools";
import { createModelApiTools } from "../tools/modelApiTools";
import {
    NOVAPOINT_MODULE_DESCRIPTORS,
    createNovapointModuleApiTools,
    parseNovapointModuleCapabilities,
} from "../tools/novapointModuleApiTools";
import { normalizeRuntimeTools, type RuntimeToolDef } from "../tools/runtimeTypes";
import { createSelectionApiTools } from "../tools/selectionApiTools";
import { createSpatialDataApiTools } from "../tools/spatialDataApiTools";
import { createTaskApiTools } from "../tools/taskApiTools";
import { createTypeCatalogApiTools } from "../tools/typeCatalogApiTools";
import { invokeCefSharpMethod } from "./cefSharpBinding";
import { bindHostObject } from "./hostObjectBinding";
import type { WorkspaceAPI } from "./workspaceService";

interface ClientApiBinding {
    category: ToolCategory;
    namespace: string;
    label: string;
    createTools: () => Record<string, RuntimeToolDef>;
}

const CLIENT_API_BINDINGS: readonly ClientApiBinding[] = [
    { category: "model", namespace: "modelApi", label: "ModelAPI", createTools: createModelApiTools },
    { category: "task", namespace: "taskApi", label: "TaskAPI", createTools: createTaskApiTools },
    { category: "feature", namespace: "featureApi", label: "FeatureAPI", createTools: createFeatureApiTools },
    { category: "selection", namespace: "selectionApi", label: "SelectionAPI", createTools: createSelectionApiTools },
    { category: "typeCatalog", namespace: "typeCatalogApi", label: "TypeCatalogAPI", createTools: createTypeCatalogApiTools },
    { category: "automation", namespace: "automationApi", label: "AutomationAPI", createTools: createAutomationApiTools },
    { category: "conversion", namespace: "conversionApi", label: "ConversionAPI", createTools: createConversionApiTools },
    { category: "spatialData", namespace: "spatialDataApi", label: "SpatialDataAPI", createTools: createSpatialDataApiTools },
];

export interface ClientToolBindingResult {
    boundNamespaces: Set<string>;
    novapointTools: Record<string, RuntimeToolDef>;
}

const isCategoryActive = (category: ToolCategory): boolean =>
    isToolEnabled(category) || isPreviewTool(category);

export async function bindClientTools(
    api: WorkspaceAPI,
): Promise<ClientToolBindingResult> {
    const boundNamespaces = new Set<string>();
    const novapointTools: Record<string, RuntimeToolDef> = {};

    for (const binding of CLIENT_API_BINDINGS) {
        if (!isCategoryActive(binding.category)) continue;

        try {
            const boundApi = await bindHostObject(binding.namespace);
            if (!boundApi) {
                console.warn(
                    `[${binding.label}] BindObjectAsync resolved but ${binding.namespace} not found on window - C# registration may be missing`,
                );
                continue;
            }

            api[binding.namespace] = boundApi;
            boundNamespaces.add(binding.namespace);
        } catch (error) {
            console.error(
                `[${binding.label}] Failed to bind ${binding.namespace}:`,
                error,
            );
        }
    }

    for (const descriptor of NOVAPOINT_MODULE_DESCRIPTORS) {
        if (!isCategoryActive(descriptor.category)) continue;

        try {
            const moduleApi = await bindHostObject(descriptor.namespace);
            if (!moduleApi) {
                console.warn(
                    `[NovapointModuleAPI] ${descriptor.namespace} was not registered by the host`,
                );
                continue;
            }

            api[descriptor.namespace] = moduleApi;
            boundNamespaces.add(descriptor.namespace);
            const capabilities = parseNovapointModuleCapabilities(
                descriptor,
                await invokeCefSharpMethod(
                    descriptor.namespace,
                    moduleApi,
                    "getCapabilities",
                    [],
                ),
            );
            Object.assign(
                novapointTools,
                createNovapointModuleApiTools(descriptor, capabilities),
            );
        } catch (error) {
            console.warn(
                `[NovapointModuleAPI] Failed to bind ${descriptor.namespace}:`,
                error,
            );
        }
    }

    return { boundNamespaces, novapointTools };
}

export function createBoundRuntimeTools(
    binding: ClientToolBindingResult,
): Record<string, RuntimeToolDef> {
    const tools: Record<string, RuntimeToolDef> = {};

    for (const clientApi of CLIENT_API_BINDINGS) {
        if (binding.boundNamespaces.has(clientApi.namespace)) {
            Object.assign(tools, clientApi.createTools());
        }
    }
    Object.assign(tools, binding.novapointTools);

    return normalizeRuntimeTools(tools);
}

export function hasBoundPreviewTool(
    boundNamespaces: Set<string>,
): boolean {
    return (
        CLIENT_API_BINDINGS.some(
            ({ category, namespace }) =>
                isPreviewTool(category) && boundNamespaces.has(namespace),
        ) ||
        NOVAPOINT_MODULE_DESCRIPTORS.some(
            ({ category, namespace }) =>
                isPreviewTool(category) && boundNamespaces.has(namespace),
        )
    );
}
